import React, { useEffect, useRef } from 'react';
import { createGlobalStyle } from 'styled-components';

import Editor, { EditorOptions } from '@toast-ui/editor';
import '@toast-ui/editor/dist/toastui-editor.css';

const ToastUIFontReset = createGlobalStyle`
  .toastui-editor-contents,
  .toastui-editor-contents p,
  .toastui-editor-contents span,
  .toastui-editor-ww-container,
  .toastui-editor-ww-container p,
  .toastui-editor-ww-container span {
    font-weight: 400 !important;
  }
`;

const ToastUIListFix = createGlobalStyle`
  .toastui-editor-contents ul {
    list-style: disc !important;          
    margin-left: 1.25rem !important;
    padding-left: 0 !important;
  }

  .toastui-editor-contents ol {
    list-style: decimal !important;       
    margin-left: 1.25rem !important;
    padding-left: 0 !important;
  }

  .toastui-editor-contents li {
    list-style-position: outside !important;
  }

  .toastui-editor-contents li::before {
    content: none !important;
  }
`;

const MoveModeSwitchToHeader = createGlobalStyle`
  .toastui-editor-defaultUI { position: relative; }
  .toastui-editor-defaultUI .toastui-editor-mode-switch {
    position: absolute;
    top: 6px;
    right: 8px;
    bottom: auto;
    z-index: 2;
    background: transparent;
    border: 0;
    padding: 0;
    margin: 0;
  }
  .toastui-editor-mode-switch .tab-item {
    padding: 2px 8px;
    font-size: 12px;
    line-height: 18px;
    height: 22px;
  }
  .toastui-editor-defaultUI .toastui-editor-main { margin-bottom: 0 !important; }
  .toastui-editor-defaultUI .toastui-editor { padding-bottom: 0 !important; }
`;

export type ToastEditorTheme = 'light' | 'dark';

export interface ToastMessageInputProps {
  value: string;
  onChange: (nextMarkdown: string) => void;
  onSend?: () => void;
  placeholder?: string;
  height?: string;
  theme?: ToastEditorTheme;
  toolbarItems?: EditorOptions['toolbarItems'];
}

const DEFAULT_TOOLBAR: EditorOptions['toolbarItems'] = [
  ['heading', 'bold', 'italic'],
  ['ul', 'ol'],
  ['link'],
];

const ToastMessageInput: React.FC<ToastMessageInputProps> = ({
  value,
  onChange,
  onSend,
  placeholder,
  height = '190px',
  theme = 'light',
  toolbarItems = DEFAULT_TOOLBAR,
}) => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const editorRef = useRef<Editor | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (!rootRef.current) return () => {};

    editorRef.current = new Editor({
      el: rootRef.current,
      initialValue: value || '',
      initialEditType: 'wysiwyg',
      previewStyle: 'tab',
      height,
      usageStatistics: false,
      placeholder,
      theme: theme === 'dark' ? 'dark' : undefined,
      toolbarItems,
      events: {
        change: () => {
          const md = editorRef.current?.getMarkdown() ?? '';
          onChangeRef.current(md);
        },
      },
    });

    return () => {
      editorRef.current?.destroy();
      editorRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the editor is created once on mount; height and value are synced by the effects below
  }, []);

  useEffect(() => {
    editorRef.current?.setHeight(height);
  }, [height]);

  useEffect(() => {
    const instance = editorRef.current;
    if (!instance) return;
    const current = instance.getMarkdown();
    if (current !== value) {
      instance.setMarkdown(value || '');
    }
  }, [value]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return () => {};

    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        onSend?.();
      }
    };

    root.addEventListener('keydown', onKeyDown);
    return () => root.removeEventListener('keydown', onKeyDown);
  }, [onSend]);

  return (
    <>
      <ToastUIListFix />
      <ToastUIFontReset />
      <MoveModeSwitchToHeader />
      <div ref={rootRef} />
    </>
  );
};

export default ToastMessageInput;
